import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable, { IDataTableColumn, IDataTableHandle } from '@srm/ui/data-table';
import Select from '@srm/ui/select';
import { IOption } from '@srm/ui/types/IOption';

import {
    ICopyAndEditAttachesAddResponse,
    ICopyAndEditAttachesData,
    ICopyAndEditAttachesItem,
    ICopyAndEditAttachesScheme,
} from '@models/attaches/ICopyAndEditAttaches';

export interface ICopyAndEditAttachFilesBlockProps {
    id?: string;
    scheme: ICopyAndEditAttachesScheme;
    data: ICopyAndEditAttachesData;
    onFilesSelected: (files: ICopyAndEditAttachesAddResponse[]) => void;
}

/** Значение свойства файла: ключи колонок приходят путём `fields.<имя>`. */
const fieldName = (key: string) => key.split('.')[1] ?? key;
const fieldValue = (row: ICopyAndEditAttachesItem, key: string) => row.fields?.[fieldName(key)];

/** Публикация правится всегда — остальное запирают признаки строки. */
const PUBLICATION_FIELD = 'fields.AttachmentPublication';

export const CopyAndEditAttachFilesBlock = (props: ICopyAndEditAttachFilesBlockProps) => {
    const [files, setFiles] = useState<ICopyAndEditAttachesItem[]>(props.data.data);
    const tableRef = useRef<IDataTableHandle<ICopyAndEditAttachesItem>>(null);
    /** Ключи, которые таблица показывает выбранными: правки выделения идут через неё. */
    const selectedRef = useRef<string[]>([]);

    const { onFilesSelected } = props;
    const updateSelectedFiles = useCallback(
        (keys: string[], rows: ICopyAndEditAttachesItem[]) => {
            const selectedData = rows.filter((file) => keys.includes(file.key));
            onFilesSelected(
                selectedData.map((data) => ({ key: data.key, fields: { ...data.fields } }) as ICopyAndEditAttachesAddResponse),
            );
        },
        [onFilesSelected],
    );

    // стартовое выделение приходит с данными (isSelected) — ставим его таблице
    useEffect(() => {
        const keys = files.filter((item) => item.isSelected).map((item) => item.key);
        selectedRef.current = keys;
        tableRef.current?.selectRows(keys);
        updateSelectedFiles(keys, files);
        // выделение из данных ставится один раз: дальше им распоряжается пользователь
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelectionChange = useCallback(
        (keys: string[]) => {
            const previous = selectedRef.current;
            const deselected = previous.filter((key) => !keys.includes(key));
            if (deselected.length === 0 && keys.length === previous.length) return;

            // строки «только для чтения» снять нельзя — возвращаем их обратно
            const readOnlyBack = deselected.filter((key) => files.some((file) => file.key === key && file.isReadOnly));
            // снятие с файла снимает и все его дубли (тот же набор существующих вложений)
            const deselectedData = files.filter((file) => deselected.includes(file.key) && !readOnlyBack.includes(file.key));
            let next = [...keys, ...readOnlyBack];
            next = next.filter((key) => {
                const existAttachKeys = files.find((file) => file.key === key)?.existAttachKeys;
                return !deselectedData.some(
                    (data) => data.isExistAttach && JSON.stringify(data.existAttachKeys) === JSON.stringify(existAttachKeys),
                );
            });

            const changed = next.length !== keys.length || next.some((key) => !keys.includes(key));
            selectedRef.current = next;
            if (changed) tableRef.current?.selectRows(next);
            updateSelectedFiles(next, files);
        },
        [files, updateSelectedFiles],
    );

    const onCellCommit = useCallback(
        (row: ICopyAndEditAttachesItem, field: string, value: unknown) => {
            setFiles((prev) => {
                const next = prev.map((item) =>
                    item.key === row.key
                        ? { ...item, fields: { ...item.fields, [fieldName(field)]: value as never } }
                        : item,
                );
                updateSelectedFiles(selectedRef.current, next);
                return next;
            });
        },
        [updateSelectedFiles],
    );

    const columns = useMemo<IDataTableColumn<ICopyAndEditAttachesItem>[]>(
        () =>
            (props.scheme.properties ?? []).map((col) => {
                const dictionary = col.dictName ? props.scheme.dictionaries?.[col.dictName] : undefined;
                return {
                    field: col.key,
                    header: col.displayName,
                    dataType: col.valueType,
                    accessor: (row: ICopyAndEditAttachesItem) => fieldValue(row, col.key),
                    allowSorting: false,
                    editable: col.isReadOnly
                        ? false
                        : (row: ICopyAndEditAttachesItem) =>
                              // автосоздаваемые и закрытые бэкендом строки правит только «Публикация»
                              col.key === PUBLICATION_FIELD || !(row.isReadOnly || row.fields?.AttachmentAutoCreate),
                    ...(dictionary
                        ? {
                              renderEditor: ({
                                  value,
                                  commit,
                              }: {
                                  value: unknown;
                                  commit: (value: unknown) => void;
                              }) => (
                                  <Select
                                      options={dictionary.map((item) => ({ value: item.code, label: item.code }))}
                                      onlySelect
                                      size="sm"
                                      // ячейка уже открыта кликом — список сразу, без второго клика
                                      defaultOpened
                                      values={
                                          value != null && value !== ''
                                              ? [{ value: String(value), label: String(value) } as IOption]
                                              : undefined
                                      }
                                      onChange={(option: IOption[]) => {
                                          if (option.length > 0) commit(option[0].value);
                                      }}
                                  />
                              ),
                          }
                        : {}),
                };
            }),
        [props.scheme],
    );

    /** Группировка приходит из схемы (уровни задаёт groupIndex). */
    const grouping = useMemo(
        () =>
            (props.scheme.properties ?? [])
                .filter((col) => typeof col.groupIndex === 'number')
                .sort((a, b) => (a.groupIndex ?? 0) - (b.groupIndex ?? 0))
                .map((col) => col.key),
        [props.scheme],
    );

    return (
        <div data-testid={props.id ? `attach-files-block-${props.id}` : undefined}>
            <DataTable<ICopyAndEditAttachesItem>
                controlRef={tableRef}
                columns={columns}
                data={files}
                getRowId={(row) => row.key}
                defaultGrouping={grouping}
                paging={false}
                stickyHeader
                selectable
                onSelectionChange={onSelectionChange}
                onCellCommit={onCellCommit}
                emptyText="Нет доступных файлов"
            />
        </div>
    );
};
